import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Activity,
  BarChart3,
  CheckCircle2,
  TrendingUp,
  Settings2,
  Users,
  Scale,
  Headphones,
  LineChart,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ProductPageLayout from '../components/ProductPageLayout';

const BACKDROP_IMAGE =
  'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/40wu0z84_TRANSACTION%20REPORTING%20BACKDROP%20IMAGE.png';

/* ─────────────────────────────────────── HERO ─────────────────────────────────────── */
const TrxReportingHero = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = ['/', 'footer'].includes(location.state?.from);
  const fromProductNav = location.state?.from === 'product-pagination';
  const productNavReturnTo = location.state?.returnTo;
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <section className="relative overflow-hidden" data-testid="trx-reporting-hero">
      {/* Left-heavy dark scrim — over the single fixed backdrop image */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(to right, rgba(8,14,24,0.45) 0%, rgba(8,14,24,0.30) 35%, rgba(8,14,24,0.10) 60%, rgba(8,14,24,0.03) 85%, rgba(8,14,24,0) 100%)',
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#0a1220]/60 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-28 sm:pb-24 lg:pt-28 lg:pb-24 min-h-[75vh] flex flex-col">
        <button
          onClick={() => {
            if (fromTopNav) navigate('/');
            else if (fromProductNav && productNavReturnTo) navigate(productNavReturnTo);
            else if (fromHome) navigate(-1);
            else navigate('/products');
          }}
          className="self-start flex items-center gap-2 text-white/85 hover:text-white font-medium mb-14 sm:mb-16 transition-colors duration-200"
          data-testid="product-back-button"
        >
          <ArrowLeft size={18} />
          {fromTopNav ? 'Back to Home' : (fromHome || fromProductNav) ? 'Back' : 'Back to Products'}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center flex-1">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/50 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.18em] mb-6 backdrop-blur-sm"
              data-testid="trx-reporting-eyebrow"
            >
              <LineChart size={12} />
              Reporting & Analytics
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-[88px] font-bold text-white leading-[1.05] tracking-tight mb-6"
              style={{ textShadow: '0 6px 28px rgba(0,0,0,0.55)' }}
              data-testid="product-title"
            >
              Transaction<br />Reporting
            </h1>

            <p
              className="text-lg sm:text-xl lg:text-2xl text-white/95 leading-relaxed max-w-2xl mb-10 font-medium"
              style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
            >
              Real-time insights to help businesses<br />
              track, analyse and grow.
            </p>

            <Link
              to="/get-started-transaction-reporting"
    onClick={() => window.__resetSourceScroll?.()}
              className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-xl hover:shadow-2xl"
              data-testid="trx-reporting-cta-button"
            >
              Get Started Here</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────── INSIGHT / REPORTING CARD ─────────────────────────────────────── */
const ReportingCard = ({ eyebrow, title, subtitle, intro, sections, accent, icon: Icon, testId }) => (
  <div
    className="flex flex-col h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/40 overflow-hidden"
    data-testid={testId}
  >
    <div className={`bg-gradient-to-br ${accent} p-6 sm:p-8`}>
      <p className="text-[11px] font-bold text-white/90 uppercase tracking-[0.2em] mb-2">{eyebrow}</p>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          <Icon size={28} className="text-white" strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">{title}</h3>
          <p className="text-white/90 mt-2 text-sm sm:text-base">{subtitle}</p>
        </div>
      </div>
    </div>

    <div className="p-6 sm:p-8 flex flex-col flex-1">
      <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-5">{intro}</p>

      <div className="space-y-5 mt-1">
        {sections.map((section, idx) => (
          <div key={idx}>
            <p className="text-[11px] font-bold text-[#1F4E79] uppercase tracking-[0.15em] mb-3">{section.title}</p>
            <ul className="space-y-2.5">
              {section.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 size={16} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800 text-[13.5px] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────── BENEFIT CARD ─────────────────────────────────────── */
const PortalBenefit = ({ title, description, testId }) => (
  <div
    className="rounded-xl bg-white/95 backdrop-blur-sm border border-white/50 hover:border-[#1F4E79]/40 hover:shadow-lg p-5 transition-all duration-200"
    data-testid={testId}
  >
    <h4 className="text-base font-bold text-gray-900 leading-snug mb-1.5">{title}</h4>
    <p className="text-[13.5px] text-gray-700 leading-relaxed">{description}</p>
  </div>
);

/* ─────────────────────────────────────── COLUMN LIST ─────────────────────────────────────── */
const ColumnList = ({ icon: Icon, title, items, testId }) => (
  <div
    className="bg-white/95 backdrop-blur-sm rounded-xl border border-white/50 shadow-lg p-6 sm:p-7"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-lg bg-[#1F4E79]/10 flex items-center justify-center">
        <Icon size={20} className="text-[#1F4E79]" strokeWidth={1.75} />
      </div>
      <h4 className="text-lg font-bold text-gray-900 leading-tight">{title}</h4>
    </div>
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <CheckCircle2 size={16} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <span className="text-gray-800 text-[14px] leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

/* ─────────────────────────────────────── WHY-CHOOSE CHIP ─────────────────────────────────────── */
const WhyCard = ({ icon: Icon, title, description, testId }) => (
  <div
    className="rounded-xl bg-white/95 backdrop-blur-sm border border-white/50 hover:border-[#1F4E79]/40 hover:shadow-lg p-5 transition-all duration-200"
    data-testid={testId}
  >
    <div className="flex items-start gap-3.5">
      <div className="flex-shrink-0 mt-0.5 w-10 h-10 rounded-lg bg-[#1F4E79]/10 flex items-center justify-center">
        <Icon size={20} className="text-[#1F4E79]" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-[15px] font-bold text-gray-900 leading-snug mb-1.5">{title}</h4>
        <p className="text-[13px] text-gray-700 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────── PAGE ─────────────────────────────────────── */
const ProductsTransactionReporting = () => {
  return (
    <ProductPageLayout
      currentId="transaction-reporting"
      customHero={<TrxReportingHero />}
      bodyBgClassName=""
      mainTestId="products-trx-reporting-page"
    >
      {/* Fixed full-bleed backdrop image + dark scrim sits behind body */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${BACKDROP_IMAGE})` }}
        aria-hidden="true"
        data-testid="trx-reporting-body-bg"
      />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#0a1220]/70 via-[#0a1220]/74 to-[#0a1220]/78" aria-hidden="true" />

      {/* Intro */}
      <div className="text-center mb-12 mt-2" data-testid="trx-reporting-intro">
        <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-3">Beyond Reporting</p>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-5"
          style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
        >
          Insights That Drive Decisions
        </h2>
        <p
          className="text-lg text-white/95 leading-relaxed max-w-3xl mx-auto font-medium"
          style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}
        >
          eCOMM's reporting platform goes beyond standard transaction data, transforming payment activity into meaningful business insights. From customer behaviour and sales trends to operational performance, our analytics tools help merchants make smarter, data-driven decisions every day.
        </p>
      </div>

      {/* Comprehensive Reporting Suite — side-by-side cards */}
      <div className="mb-14" data-testid="reporting-suite">
        <div className="text-center mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Comprehensive Reporting Suite</p>
          <h2
            className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight"
            style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
          >
            Live Data Meets Advanced Intelligence
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4 mx-auto" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ReportingCard
            testId="reporting-real-time"
            eyebrow="Real-Time Transaction Analytics"
            title="See Your Business As It Happens"
            subtitle="Live dashboards, instant alerts and operational insights in real time."
            intro="Monitor transactions as they happen with live dashboards that provide instant visibility into payment activity, performance trends and operational insights in real time."
            sections={[
              {
                title: 'Key Features',
                items: [
                  'Real-time visibility of transaction activity',
                  'Access to current and historical transaction data',
                  'Filter and search transactions quickly',
                  'View performance across locations and terminals',
                  'Mobile-responsive access across devices',
                ],
              },
            ]}
            accent="from-[#0a1929] to-[#163a5f]"
            icon={Activity}
          />

          <ReportingCard
            testId="reporting-pos"
            eyebrow="Point Of Sale Reporting"
            title="Advanced Business Intelligence"
            subtitle="Transform transaction data into meaningful business insights."
            intro="Advanced insights for payment and transaction performance — designed to help merchants monitor performance, identify trends and improve operational decision-making."
            sections={[
              {
                title: 'Business Overview',
                items: [
                  'Instant access to transaction data',
                  'Automated monthly statements',
                  'Export transaction reports',
                ],
              },
              {
                title: 'Advanced Insights',
                items: [
                  'Detailed transaction filtering',
                  'Payment method analysis',
                  'Historical transaction records',
                ],
              },
            ]}
            accent="from-[#1F4E79] to-[#0E88A3]"
            icon={BarChart3}
          />
        </div>
      </div>

      {/* Merchant Portal — wrapped in one cohesive glass-morph container */}
      <div
        className="mb-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 shadow-2xl p-6 sm:p-10"
        data-testid="merchant-portal"
      >
        <div className="mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Centralised Control</p>
          <h2
            className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight"
            style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
          >
            Merchant Portal
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
          <p
            className="text-white/95 mt-5 leading-relaxed max-w-3xl"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}
          >
            The eCOMM Merchant Portal provides businesses with a centralised platform for managing transactions, monitoring performance and accessing operational tools in real time. Designed for ease of use and accessible from any device, the portal gives merchants complete visibility and control over their payment environment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start mb-7">
          <PortalBenefit
            testId="portal-anywhere"
            title="Anytime, Anywhere Access"
            description="Access your business data anytime through our secure cloud-based platform."
          />
          <PortalBenefit
            testId="portal-ux"
            title="Intuitive User Experience"
            description="Simple, clear interface designed with merchants for maximum usability and efficiency."
          />
          <PortalBenefit
            testId="portal-export"
            title="Export Options"
            description="Export transaction reports in CSV format for further analysis and record keeping."
          />
        </div>

        <div
          className="rounded-xl bg-white/95 backdrop-blur-sm border border-white/50 shadow-lg p-6 sm:p-7"
          data-testid="portal-enhanced"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#ef5b25]/15 flex items-center justify-center">
              <Settings2 size={20} className="text-[#ef5b25]" strokeWidth={1.75} />
            </div>
            <h4 className="text-lg font-bold text-gray-900 leading-tight">Enhanced Functionality</h4>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {[
              'Customer payment history and account management',
              'Recurring billing management and automation',
              'Customisable automated report scheduling',
              'Multi-user access and permissions',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 size={16} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
                <span className="text-gray-800 text-[14px] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Transform Data Into Action */}
      <div className="mb-14" data-testid="transform-action">
        <div className="mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Transform Data Into Action</p>
          <h2
            className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight"
            style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
          >
            From Numbers To Outcomes
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ColumnList
            testId="transaction-visibility"
            icon={TrendingUp}
            title="Transaction Visibility"
            items={[
              'View transaction volumes over time',
              'Compare locations and terminals',
              'Analyse payment method activity',
              'Access historical reports',
            ]}
          />
          <ColumnList
            testId="operational-efficiency"
            icon={ShieldCheck}
            title="Operational Efficiency"
            items={[
              'Simplify reconciliation processes',
              'Identify exceptions more quickly',
              'Access statements and transaction history',
              'Improve day-to-day reporting',
            ]}
          />
        </div>
      </div>

      {/* Dashed divider transition */}
      <div className="mb-10 flex items-center gap-4" data-testid="why-divider">
        <div className="flex-1 border-t border-dashed border-white/20" />
        <div className="w-1.5 h-1.5 rounded-full bg-[#ef5b25]/70" />
        <div className="flex-1 border-t border-dashed border-white/20" />
      </div>

      {/* Why Choose eCOMM Analytics */}
      <div className="mb-12" data-testid="why-ecomm-analytics">
        <div className="mb-8">
          <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-2">Why Choose eCOMM Analytics</p>
          <h2
            className="text-3xl sm:text-[2.25rem] font-bold text-white leading-tight tracking-tight"
            style={{ textShadow: '0 3px 14px rgba(0,0,0,0.55)' }}
          >
            Built For Smarter Commerce
          </h2>
          <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
          <p
            className="text-white/95 mt-5 leading-relaxed max-w-3xl"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}
          >
            As customer purchasing behaviour continues to evolve rapidly, access to sophisticated reporting tools is essential for business success.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <WhyCard
            testId="why-merchant-driven"
            icon={Users}
            title="Merchant-Driven Development"
            description="Built in close collaboration with merchants, ensuring every feature adds real value to your daily operations."
          />
          <WhyCard
            testId="why-scalable"
            icon={Scale}
            title="Scalable Intelligence"
            description="Whether you're processing dozens or thousands of transactions daily, our platform scales with your business needs."
          />
          <WhyCard
            testId="why-expert-support"
            icon={Headphones}
            title="Expert Support"
            description="Our team helps merchants maximise the value of their reporting and analytics tools."
          />
          <WhyCard
            testId="why-mobile-ready"
            icon={Smartphone}
            title="Mobile-Ready Access"
            description="Live dashboards, alerts and exports across every device — your business in your pocket."
          />
        </div>
      </div>

      {/* Final CTA strip */}
      <div
        className="rounded-xl bg-gradient-to-br from-[#0a1929] to-[#163a5f] p-8 sm:p-12 text-center shadow-lg mb-2"
        data-testid="trx-reporting-final-cta"
      >
        <h3 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-4">
          Ready To Transform Your Business?
        </h3>
        <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
          Discover how our comprehensive reporting service can help you understand your customers better, optimise operations and achieve your business objectives.
        </p>
        <Link
          to="/get-started-transaction-reporting"
    onClick={() => window.__resetSourceScroll?.()}
          className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
          data-testid="trx-reporting-final-cta-button"
        >
          Get Started Here</Link>
      </div>
    </ProductPageLayout>
  );
};

export default ProductsTransactionReporting;
